import mapperIcon from '../data-layer/utils/mappers/mapper-icon'

interface FilterOptionItem {
  id: string
  text?: string
  value?: string
}
type FilterOptionsMap = {
  [key: string]: FilterOptionItem[]
}

type Filter = { name: string; value: string | string[] }

export const useFilters = (emit: Function) => {
  const router = useRouter()
  const route = useRoute()
  const filters = useState('filters-filters', () => [])
  const query = useState('filters-query', () => '')
  const filterOptions = ref<FilterOptionsMap>({})

  // initialized null, as the watch can be active on demand
  let stopWatchFilters: (() => void) | null = null

  const getIcon = (icon, defaultIcon) => {
    if (!icon && defaultIcon) {
      return defaultIcon
    }
    return icon
  }

  const getFilterValue = (filterName, multiple: boolean, initialFilter) => {
    // InitialFilter -> in this case, the user may select a filter from the knowledge hub homepage that will direct them to the articles page with a query parameter
    // Prior to this addition this value never gets passed into the getFilterValue method, so the select boxes never showed which filters were selected.
    if (initialFilter) {
      return initialFilter?.find((i) => i.name === filterName)?.value
    }

    const value = multiple ? [] : ''
    return filters.value?.find((i) => i.name === filterName)?.value || value
  }

  const setFilter = (filterName, filterValue) => {
    const filters_ = [...filters.value]
    const index = filters_.findIndex(({ name }) => name === filterName)
    // filter exists and value is empty
    if ((filterValue === '' || filterValue.length === 0) && index !== -1) {
      // remove item from array
      filters_.splice(index, 1)
    } else if (index !== -1) {
      // filter exists update value
      filters_.splice(index, 1, { name: filterName, value: filterValue })
    } else {
      // new filter push value
      filters_.push({ name: filterName, value: filterValue })
    }
    filters.value = filters_
  }

  const removeFilter = (filterName) => {
    const filters_ = [...filters.value]
    // get filter index and remove item from array
    const index = filters_.findIndex(({ name }) => name === filterName)
    if (index !== -1) {
      filters_.splice(index, 1)
    }
    filters.value = filters_
  }

  const resetFilters = () => {
    filters.value = []
  }

  // Use unsafe flag if text might contain slashes
  const handleSearch = (value: any) => {
    query.value = value
    emit('search', value)
  }

  const normalizeExternalFilterOptions = (items, defaultIcon) => {
    return (
      items?.map((item) => {
        const icon = item.icon ? getIcon(item?.icon, defaultIcon) : null
        return {
          id: item.id,
          text: item.name,
          value: item.id,
          ...(icon && { icon: mapperIcon(icon) })
        }
      }) || []
    )
  }

  const normalizeFilterOptions = (data) => {
    if (!data) return []
    return data.map((item) => ({ id: item.sys?.id || item.id, value: item.sys?.id || item.id, text: item.name }))
  }

  const normalizeKnowledgeHubFilterOptions = (items, defaultIcon) => {
    return items?.map((item) => {
      const icon = getIcon(item?.icon, defaultIcon)
      return {
        id: item?.sys?.id,
        text: item.name,
        value: item?.sys?.id,
        ...(icon && { icon: mapperIcon(icon) })
      }
    })
  }

  /**
   * Given an entry ID it will return the `name` field of that entry
   * @param {String} name - Filter name
   * @param {String} id - Entry id
   * @returns {String} - Returns entry field name or id if there was no entry match
   */
  const encodeQueryParam = (name: string, id: string): string => {
    return encodeURIComponent(filterOptions.value[name]?.find((item) => item?.id === id)?.text || id)
  }

  /**
   * Given a filter name and an encoded query parameter value,
   * it will return the original ID of the filter option.
   * @param {String} name - Filter name (e.g., 'categories', 'colors')
   * @param {String} value - The encoded query parameter value from the URL
   * @returns {String} - Returns the original entry ID or the decoded value if no match was found
   */
  const decodeQueryParam = (name: string, value: string): string => {
    const decodedValue = decodeURIComponent(value)
    return (
      filterOptions.value[name]?.find((item) => item?.text?.toLowerCase() === decodedValue?.toLowerCase())?.id || value
    )
  }

  /**
   * Decodes and initializes filters from existing URL query parameters.
   * @param {string[]} singleFilterIds - List of query param names that are single filters.
   * @param {string[]} multipleFilterIds - List of query param names that are multiple filters.
   */
  const initializeFiltersFromQueryParams = (singleFilterIds: string[], multipleFilterIds: string[]) => {
    // Identify which query parameters are actually filter parameters
    const queryParamsFilterKeys = Object.keys(route.query).filter((queryParamName) =>
      singleFilterIds.includes(queryParamName)
    )

    if (!queryParamsFilterKeys.length) {
      return
    }

    // Process each filter query parameter
    queryParamsFilterKeys.forEach((queryParamName) => {
      const queryParamValue = route?.query?.[queryParamName]

      const valuesToDecode: string[] = Array.isArray(queryParamValue)
        ? queryParamValue.filter((v): v is string => typeof v === 'string') // Filter out null/undefined from array
        : typeof queryParamValue === 'string'
          ? queryParamValue.split(',') // It's a single string, split it
          : []
      const decodedValues = valuesToDecode.map((val: string) => decodeQueryParam(queryParamName, val))

      const filterValue = multipleFilterIds.includes(queryParamName) ? decodedValues : decodedValues[0]

      // Set the filter state
      setFilter(queryParamName, filterValue)
    })
  }

  /**
   * Prepares an object of query parameters from the current filters state.
   * @param {Filter[]} currentFilters - The current active filters.
   * @param {string[]} singleFilterIds - List of query param names that are single filters.
   * @returns {Record<string, string | string[]>} - An object mapping filter names to their encoded query param values.
   */
  const prepareQueryParamsFromFilters = (
    currentFilters: Filter[],
    singleFilterIds: string[] // You might need this to know what to keep/remove
  ): Record<string, string | string[]> => {
    // Keep query params that are not filters
    const existingNonFilterQueryParams = Object.keys(route.query).reduce(
      (acc: Record<string, string | string[]>, queryParamName) => {
        if (!singleFilterIds.includes(queryParamName)) {
          acc[queryParamName] = route.query[queryParamName]
        }
        return acc
      },
      {}
    )

    // Map filters to their encoded query param format
    const encodedFilterQueryParams = currentFilters.reduce((acc: Record<string, string | string[]>, item) => {
      if (item.value) {
        // Only add if filter has a value
        const encodedValue = Array.isArray(item.value)
          ? item.value.map((val) => encodeQueryParam(item.name, val)).join(',')
          : encodeQueryParam(item.name, item.value)
        acc[item.name] = encodedValue
      }
      return acc
    }, {})

    return {
      ...existingNonFilterQueryParams,
      ...encodedFilterQueryParams
    }
  }

  /**
   * Configures and starts the watcher to synchronize filter changes with URL query parameters.
   * This is called by `enableQueryParams`.
   * @param {string[]} singleFilterIds - List of query param names that are single filters.
   */
  const setupFilterWatcher = (singleFilterIds: string[]) => {
    stopWatchFilters = watch(
      filters,
      (newValue) => {
        const newQueryParams = prepareQueryParamsFromFilters(newValue, singleFilterIds)

        // Set query params to the url
        router.push({
          path: route.path,
          query: newQueryParams
        })
      },
      {
        deep: true
      }
    )
  }

  /**
   * Enables the automatic update of query parameters when filters change.
   * This function sets up initial filter state from URL and starts the watcher.
   * @param {Object} options - Configuration options.
   * @param {FilterOptionsMap} options.filterOptions - The map of available filter options.
   * @param {string[]} [options.singleFilterIds=[]] - Array of filter names that represent single-select filters.
   * @param {string[]} [options.multipleFilterIds=[]] - Array of filter names that represent multi-select filters.
   */
  const enableQueryParams = ({
    filterOptions: filterOptionsParam,
    singleFilterIds = [],
    multipleFilterIds = []
  }: {
    filterOptions: FilterOptionsMap
    singleFilterIds?: string[]
    multipleFilterIds?: string[]
  }) => {
    // 1. Set the available filter options
    filterOptions.value = filterOptionsParam

    // 2. Initialize filters from existing query params
    initializeFiltersFromQueryParams(singleFilterIds, multipleFilterIds)

    // 3. Set up the watcher
    if (!stopWatchFilters) {
      // Starting to watch filters for query params updates
      setupFilterWatcher(singleFilterIds)
    }
  }

  onUnmounted(() => {
    // Clean watch
    if (stopWatchFilters) {
      stopWatchFilters()
      stopWatchFilters = null
    }
  })

  return {
    getFilterValue,
    normalizeExternalFilterOptions,
    normalizeFilterOptions,
    normalizeKnowledgeHubFilterOptions,
    setFilter,
    handleSearch,
    filters,
    query,
    resetFilters,
    removeFilter,
    enableQueryParams
  }
}
